<template>
  <nav class="fixed top-0 left-0 right-0 z-50 transition-all duration-500 ease-in-out" :class="navClasses">
    <div class="relative w-full">
      <div class="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-echoes-border-light to-transparent"></div>
      <div class="max-w-7xl mx-auto px-4 sm:px-6">
        <div class="flex items-center transition-all duration-500 ease-in-out" :style="{ height: navHeight }">
        <!-- Desktop Layout -->
        <div class="hidden md:flex items-center w-full justify-between">
          <!-- Logo -->
          <div class="flex-shrink-0">
            <router-link to="/" class="block">
              <img 
                src="../../assets/images/logo-dark.png" 
                alt="PortexAI" 
                class="transition-all duration-500 ease-in-out"
                :style="{ height: logoHeight }"
              >
            </router-link>
          </div>

          <!-- Right Side Content -->
          <div class="flex items-center space-x-12">
            <!-- Navigation Links -->
            
              <a href="#" class="text-gray-300 hover:text-white  text-sm font-medium transition-colors" @click="redirectToMarketplace('explore')">
                Datalab
              </a>
              <router-link to="/composite" class="text-gray-300 hover:text-white text-sm font-medium transition-colors">
                COMPOSITE
              </router-link>
              <a href="https://research.portexai.com/" target="_blank" class="text-gray-300 hover:text-white  text-sm font-medium transition-colors">
                Research
              </a>
              <a href="https://docs.portexai.com/" target="_blank" class="text-gray-300 hover:text-white  text-sm font-medium transition-colors">
                Docs
              </a>
               <!-- Auth Buttons -->
            
              <a href="#" class="text-gray-300 hover:text-white text-sm font-medium transition-colors" @click="redirectToMarketplace('login')">
                Sign In
              </a>
              <a href="#" class="nav-cta-button relative inline-flex items-center justify-center text-sm font-semibold text-white transition-all duration-300 hover:bg-white hover:text-black" @click="redirectToMarketplace('register')">
                <span class="relative z-10">Register</span>
              </a>
            
            
           
          </div>
        </div>

        <!-- Mobile Layout -->
        <div class="md:hidden flex items-center justify-between w-full">
          <!-- Empty left space for balance -->
          <div class="w-12"></div>
          
          <!-- Centered Logo -->
          <div class="flex-shrink-0">
            <router-link to="/" class="block">
              <img 
                src="../../assets/images/logo-dark.png" 
                alt="PortexAI" 
                class="transition-all duration-500 ease-in-out"
                :style="{ height: logoHeight }"
              >
            </router-link>
          </div>

          <!-- Mobile menu button with background -->
          <div class="mobile-menu-button-container">
            <button @click="mobileMenuOpen = !mobileMenuOpen" class="mobile-menu-button text-gray-300 hover:text-white p-2 transition-all duration-300">
              <svg class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path v-if="!mobileMenuOpen" stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 6h16M4 12h16M4 18h16" />
                <path v-else stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>

    <!-- Mobile menu -->
    <div v-if="mobileMenuOpen" class="md:hidden bg-black/20 backdrop-blur-xl relative">
      <div class="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-echoes-border-light to-transparent"></div>
      <div class="px-4 pt-2 pb-3 space-y-1">
        <a href="#" class="block text-gray-300 hover:text-white px-3 py-2 text-base font-medium transition-colors" @click="redirectToMarketplace('explore')">
          Datalab
        </a>
        <router-link to="/composite" class="block text-gray-300 hover:text-white px-3 py-2 text-base font-medium transition-colors">
          COMPOSITE
        </router-link>
        <a href="https://research.portexai.com/" target="_blank" class="block text-gray-300 hover:text-white px-3 py-2 text-base font-medium transition-colors">
          Research
        </a>
        <a href="https://docs.portexai.com/" target="_blank" class="block text-gray-300 hover:text-white px-3 py-2 text-base font-medium transition-colors">
          Docs
        </a>
        <div class="border-t border-gray-800 pt-3">
          <a href="#" class="block text-gray-300 hover:text-white px-3 py-2 text-base font-medium transition-colors" @click="redirectToMarketplace('login')">
            Sign In
          </a>
          <a href="#" class="nav-cta-button-mobile block mt-2 relative inline-flex items-center justify-center px-3 py-2 text-base font-medium text-white transition-all duration-300" @click="redirectToMarketplace('register')">
            <span class="relative z-10">Get Started Free</span>
          </a>
        </div>
      </div>
    </div>
  </nav>
</template>

<script setup>
import { ref, onMounted, onUnmounted, computed } from 'vue'
import { handleMarketplaceUrl } from '@/services/api';

const marketplaceUrl = handleMarketplaceUrl();

const mobileMenuOpen = ref(false)
const scrollY = ref(0)

const updateScroll = () => {
  scrollY.value = window.scrollY
}

// Computed properties for responsive sizing
const navHeight = computed(() => {
  const baseHeight = 72 // 64 * 1.25 (25% increase)
  const maxHeight = 72 // 96 * 1.25 (25% increase)
  const scrollThreshold = 200
  
  if (scrollY.value === 0) return `${maxHeight}px`
  if (scrollY.value >= scrollThreshold) return `${baseHeight}px`
  
  // Smooth transition between max and base height
  const progress = scrollY.value / scrollThreshold
  const currentHeight = maxHeight - (maxHeight - baseHeight) * progress
  return `${currentHeight}px`
})

const logoHeight = computed(() => {
  const baseHeight = 28 // Kept same for scrolled state
  const maxHeight = 28 // Reduced by ~30% from 48px for initial load
  const scrollThreshold = 200
  
  if (scrollY.value === 0) return `${maxHeight}px`
  if (scrollY.value >= scrollThreshold) return `${baseHeight}px`
  
  // Smooth transition between max and base height
  const progress = scrollY.value / scrollThreshold
  const currentHeight = maxHeight - (maxHeight - baseHeight) * progress
  return `${currentHeight}px`
})

const navClasses = computed(() => {
  const baseClasses = 'transition-all duration-500 ease-in-out bg-black'
  
  if (scrollY.value === 0) {
    // Initial state - completely transparent, no background

    return `${baseClasses}`
  } else if (scrollY.value < 200) {
    // Transitioning state - start adding transparency
    return `${baseClasses} backdrop-blur-xl bg-white/8 shadow-lg bg-transparent`
  } else {
    // Scrolled state - full liquid glass effect
    return `${baseClasses} backdrop-blur-xl bg-white/12 shadow-xl bg-transparent`
  }
})

const redirectToMarketplace = (path) => {
  const url = {
    'explore': marketplaceUrl + '/explore',
    'login': marketplaceUrl + '/login',
    'register': marketplaceUrl + '/register',
  }
  window.location.href = url[path];
  return window.location.href;
}

onMounted(() => {
  window.addEventListener('scroll', updateScroll, { passive: true })
  updateScroll() // Initialize scroll position
})

onUnmounted(() => {
  window.removeEventListener('scroll', updateScroll)
})
</script>

<style scoped>
/* Nav CTA Button - smaller version of hero button without glow */
.nav-cta-button {
  background: #000000;
  border-radius: 25px;
  padding: 1px;
  position: relative;
  overflow: visible;
  min-width: 140px;
  height: 36px;
}

.nav-cta-button::before {
  content: '';
  position: absolute;
  top: -1px;
  left: -1px;
  right: -1px;
  bottom: -1px;
  border-radius: 26px;
  background: linear-gradient(
    45deg,
    #ffffff,
    #c0c0c0,
    #ffffff,
    #c0c0c0,
    #ffffff
  );
  background-size: 300% 300%;
  animation: borderGradient 3s ease-in-out infinite;
  z-index: -2;
}

.nav-cta-button::after {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: #000000;
  border-radius: 25px;
  z-index: -1;
}

.nav-cta-button:hover::before {
  animation: borderGradientFast 2s ease-in-out infinite;
}

/* Mobile CTA Button */
.nav-cta-button-mobile {
  background: #000000;
  border-radius: 8px;
  padding: 1px;
  position: relative;
  overflow: visible;
  width: 100%;
}

.nav-cta-button-mobile::before {
  content: '';
  position: absolute;
  top: -1px;
  left: -1px;
  right: -1px;
  bottom: -1px;
  border-radius: 9px;
  background: linear-gradient(
    45deg,
    #ffffff,
    #c0c0c0,
    #ffffff,
    #c0c0c0,
    #ffffff
  );
  background-size: 300% 300%;
  animation: borderGradient 3s ease-in-out infinite;
  z-index: -2;
}

.nav-cta-button-mobile::after {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: #000000;
  border-radius: 8px;
  z-index: -1;
}

@keyframes borderGradient {
  0%, 100% {
    background-position: 0% 50%;
  }
  50% {
    background-position: 100% 50%;
  }
}

@keyframes borderGradientFast {
  0%, 100% {
    background-position: 0% 50%;
  }
  50% {
    background-position: 100% 50%;
  }
}

/* Mobile Menu Button Styles */
.mobile-menu-button-container {
  position: relative;
  border-radius: 8px;
  padding: 1px;
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.2);
}

.mobile-menu-button {
  background: rgba(0, 0, 0, 0.3);
  backdrop-filter: blur(5px);
  border-radius: 7px;
  border: none;
  outline: none;
}
</style>
